import { ComponentProps, ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { Tooltip, TooltipContent, TooltipOrigin } from "../tooltip";
import {
  DescriptionVariant,
  useDescriptionVariant,
} from "./use-description-variant";
import { EmptyText } from "../empty-text";
import { HelpIcon } from "../../icons";
import { ExternalLink, ExternalLinkProps } from "../link";

type Placement = ComponentProps<typeof Tooltip>["placement"];

interface TooltipDescriptionProps {
  placement?: Placement;
  trigger?: ReactNode;
  className?: string;
  children?: ReactNode;
}

/**
 * @experimental
 */
export const TooltipDescription = ({
  placement = "right",
  trigger,
  className,
  children,
}: TooltipDescriptionProps): JSX.Element | null => {
  const descriptionVariant = useDescriptionVariant();

  if (descriptionVariant !== "tooltip") {
    return null;
  }

  return (
    /**
     * inline-flex + the EmptyText ensures that the icon is vertically centered even when text wraps.
     */
    <Tooltip placement={placement}>
      <TooltipOrigin className="inline-flex items-center">
        <EmptyText />
        {trigger ?? <HelpIcon className={"w-4 h-4 text-shale-400"} />}
      </TooltipOrigin>
      <TooltipContent>
        <TooltipDescriptionContent className={className}>
          {children}
        </TooltipDescriptionContent>
      </TooltipContent>
    </Tooltip>
  );
};

interface TooltipDescriptionContentProps {
  className?: string;
  children?: ReactNode;
}

export const TooltipDescriptionContent = ({
  className,
  children,
}: TooltipDescriptionContentProps): JSX.Element => {
  return (
    <div className={twMerge("text-base text-white", className)}>{children}</div>
  );
};

interface StaticDescriptionProps {
  className?: string;
  children?: ReactNode;
}

/**
 * @experimental
 */
export const StaticDescription = (
  props: StaticDescriptionProps
): JSX.Element | null => {
  const descriptionVariant = useDescriptionVariant();

  if (descriptionVariant !== "static") {
    return null;
  }

  return <StaticDescriptionContent {...props} />;
};

interface StaticDescriptionContentProps {
  className?: string;
  children?: ReactNode;
}

export const StaticDescriptionContent = ({
  className,
  children,
}: StaticDescriptionContentProps): JSX.Element => {
  return (
    <span className={twMerge("text-sm text-shale-200", className)}>
      {children}
    </span>
  );
};

type DescriptionExternalLinkProps = Omit<ExternalLinkProps, "size">;

const sizeMap: Record<DescriptionVariant, ExternalLinkProps["size"]> = {
  tooltip: "normal",
  static: "small",
};

/**
 * @experimental
 *
 * Same as `ExternalLink`, but handles the size based on the description variant
 * (tooltip vs. static).
 */
export const DescriptionExternalLink = (
  props: DescriptionExternalLinkProps
): JSX.Element => {
  const descriptionVariant = useDescriptionVariant();

  return <ExternalLink {...props} size={sizeMap[descriptionVariant]} />;
};
